// One section's live store and its unpublished changes. Used by the three section hooks
import { useCallback, useRef, useState } from "react"
import { overlay, pendingKey, PendingSync, publishInto, splitKey, stage, type PendingArea, type PendingMap, type Staged } from "./persistence/staging"

export function useStaging<S>(
  area: PendingArea, buckets: readonly string[], initial: S,
  // Whether a record is on the public site; edits to records that aren't apply straight away
  isLive?: (bucket: string, entity: any) => boolean,
) {
  const liveRef = useRef<S>(initial)
  const pendingRef = useRef<PendingMap>({})
  const syncRef = useRef<PendingSync>(null as any)
  if (!syncRef.current) syncRef.current = new PendingSync(area)
  // Rendered state: what's waiting, and the live store for things that must only see published content
  const [pendingKeys, setPendingKeys] = useState<string[]>([])
  const [liveStore, setLiveStore] = useState<S>(initial)
  const [available, setAvailable] = useState(false)

  const publishState = (live: S, pending: PendingMap) => {
    liveRef.current = live
    pendingRef.current = pending
    setLiveStore(live)
    setPendingKeys(Object.keys(pending))
  }

  // Team switch: nothing loaded yet
  const reset = useCallback((fresh: S, team: string) => {
    syncRef.current.configure(team)
    setAvailable(false)
    publishState(fresh, {})
  }, [])

  // After the live rows load: fetch this team's unpublished changes and return what to show
  const hydrate = useCallback(async (live: S): Promise<S> => {
    const pending = await syncRef.current.load()
    setAvailable(syncRef.current.available)
    publishState(live, pending)
    return overlay(live, pending, buckets)
  }, [buckets])

  // Splits a change; nothing is applied until commit, so a refused change leaves no trace
  const split = useCallback((prevWorking: S, nextWorking: S, isManager: boolean): Staged<S> => {
    if (!syncRef.current.available) {
      return { live: nextWorking, pending: {}, changedPending: [], liveChanged: true }
    }
    return stage(liveRef.current, pendingRef.current, prevWorking, nextWorking, buckets, { reorderLive: isManager, isLive })
  }, [buckets, isLive])

  const commit = useCallback((staged: Staged<S>) => {
    for (const key of staged.changedPending) syncRef.current.queue(key, staged.pending[key] ?? null)
    publishState(staged.live, staged.pending)
  }, [])

  // The live store once these changes are published, and the working store after
  const publishPlan = useCallback((keys: string[]) => {
    const live = publishInto(liveRef.current, pendingRef.current, keys)
    const pending = { ...pendingRef.current }
    for (const k of keys) delete pending[k]
    return { live, pending, working: overlay(live, pending, buckets) }
  }, [buckets])

  const applyPlan = useCallback((plan: { live: S; pending: PendingMap }, keys: string[]) => {
    for (const k of keys) syncRef.current.queue(k, null)
    publishState(plan.live, plan.pending)
  }, [])

  // Drops changes: the working store goes back to live for those records
  const discardPlan = useCallback((keys: string[]) => {
    const pending = { ...pendingRef.current }
    for (const k of keys) delete pending[k]
    return { live: liveRef.current, pending, working: overlay(liveRef.current, pending, buckets) }
  }, [buckets])

  const keyOf = useCallback((bucket: string, id: string) => pendingKey(bucket, id), [])
  const pendingEntity = useCallback((key: string) => pendingRef.current[key], [])
  const liveEntity = useCallback((bucket: string, id: string) => (liveRef.current as any)[bucket]?.[id], [])

  return {
    available,
    pendingKeys,
    liveStore,
    live: () => liveRef.current,
    allKeys: () => Object.keys(pendingRef.current),
    reset, hydrate, split, commit,
    publishPlan, applyPlan, discardPlan,
    keyOf, splitKey, pendingEntity, liveEntity,
    setAuthor: (fn: () => { name?: string; email?: string }) => { syncRef.current.author = fn },
    flush: () => syncRef.current.flush(),
  }
}

export type Staging<S> = ReturnType<typeof useStaging<S>>
